import type { NavAccentColor } from "@ui/lib/accent-classes";
import { accentScope } from "@ui/lib/accent-scope";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import { tv } from "@ui/lib/tv";
import { Link, type LinkProps } from "@ui/primitives/Link";
import { composeRenderProps } from "@ui/primitives/shared";
import type { AriaAttributes, ReactNode } from "react";
import NavTail from "./assets/nav-tail.svg?react";

const topBarNavLink = tv({
  slots: {
    root: "group relative inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-secondary no-underline outline-none focus-visible:shadow-focus-ring",
    icon: "shrink-0 size-8 origin-center motion-reduce:animate-none",
    label: "text-lg/6 font-medium whitespace-nowrap",
    // currentColor tracks the pill's own background channel so the tail always matches it.
    tail: "pointer-events-none absolute top-full left-1/2 -mt-0.5 h-[11px] w-6 -translate-x-1/2 text-(--tv-bg-primary)",
  },
  variants: {
    isCurrent: {
      false: { icon: "group-hover:animate-wiggle" },
      true: { root: "bg-primary text-primary" },
    },
  },
});

export interface TopBarNavLinkProps extends LinkProps {
  /** RAC derives its `isCurrent` render prop from this; declared so the accent scope can read it too. */
  "aria-current"?: AriaAttributes["aria-current"];
  color?: NavAccentColor;
  leadIcon: ReactNode;
}

export function TopBarNavLink({
  color = "purple",
  leadIcon,
  prefetch = "hover",
  cacheFor = NAV_PREFETCH_CACHE_FOR,
  ...props
}: TopBarNavLinkProps) {
  // Figma's Default state is Neutral; only Selected is drawn in the item's accent.
  const current = props["aria-current"];
  const isCurrent = current !== undefined && current !== false;

  return (
    <Link
      {...props}
      {...accentScope(isCurrent ? color : undefined)}
      prefetch={prefetch}
      cacheFor={cacheFor}
      className={composeRenderProps(props.className, (className, renderProps) =>
        topBarNavLink({ isCurrent: renderProps.isCurrent }).root({ className }),
      )}
    >
      {composeRenderProps(props.children, (children, renderProps) => {
        const styles = topBarNavLink({ isCurrent: renderProps.isCurrent });
        return (
          <>
            <span aria-hidden="true" className={styles.icon()}>
              {leadIcon}
            </span>
            <span className={styles.label()}>{children}</span>
            {renderProps.isCurrent && <NavTail aria-hidden="true" className={styles.tail()} />}
          </>
        );
      })}
    </Link>
  );
}
